import React, { useState, useEffect, useRef, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useParams, useSearchParams } from "react-router-dom";
import { useBoardUsers } from "../../../../../../utils/reactQuery/users";
import { updateComment } from "../../../../../../sevices/apiComment";
import { useResponsive } from "../../../../../../hooks/useResponsive";
import { removeHtmlTagsAndEntities } from "../../../../../../utils/helpers";
import CommentEditor from "../components/CommentEditor";
import "./editComment.scss";
import useMutate from "../../../../../../hooks/useMutate";
import useBoardIdByKey from "../../../../../../hooks/useBoardIdByKey";

export default React.memo( function EditTextFields({ cardId, messg, comment_id, setShowEdit }) {
  const queryClient = useQueryClient();
  const [newComment, setNewComment] = useState(messg);
  const isMobile = useResponsive("down", "md");
  const { id } = useBoardIdByKey();
  const [searchParams] = useSearchParams();
  const { data } = useBoardUsers(searchParams.get("brd_id") || id);
  const [selectedUserForTag, setSelectedUserForTag] = useState([]);
  const [mentionListVisible, setMentionListVisible] = useState(false);
  const [elementPos, setElementPos] = useState();
  const [cursorPosition, setCursorPosition] = useState(0);
  const [anchorEl, setAnchorEl] = useState(null);
  const editorRef = useRef(null);
  const [selectedId, setSelectedId] = useState([]);
  const [sortUser, setSortUser] = useState("");
  const [isDisable, setIsDisable] = useState(true);

  const { mutate: updateCommentMutate } = useMutate(updateComment, [],
    ()=>{},
    () => {
      queryClient.invalidateQueries({
        queryKey: [`comment`, cardId],
      });
      queryClient.invalidateQueries({
        queryKey: [`history`, String(cardId)],
      });
    },
    ()=>{},
  )

  const handleSave = useCallback(() => {
    if (newComment && !isDisable) {
      const helper = selectedId?.filter(ele => 
        data?.data?.data?.responseData?.some(user => 
          user.user_id === ele && 
          newComment?.toLowerCase()?.includes(user.username?.toLowerCase())
        )
      );

      updateCommentMutate({
        comment_id: comment_id,
        comment: newComment,
        tagged_user: [...new Set(helper)]?.join(",") || null,
      });
      setShowEdit(false);
    }
  }, [newComment, isDisable, selectedId, data, updateCommentMutate, comment_id, setShowEdit]);

  const handleMention = useCallback((cursorPos) => {
    const anchorEl = document.getElementById("forAnchor1");
    setElementPos(prev => ({
      top: prev?.top + anchorEl.getBoundingClientRect().top,
      left: prev?.left + anchorEl.getBoundingClientRect().left,
    }));
    setCursorPosition(cursorPos - 1);
    setAnchorEl(anchorEl);
    setMentionListVisible(true);
  }, []);

  const handleUserSelect = useCallback((user) => {
    const quillEditor = editorRef?.current?.getEditor();
    setSelectedId(prev => [...prev, user?.user_id]);

    quillEditor.deleteText(cursorPosition, 1);
    quillEditor.insertText(cursorPosition, `@${user?.username} `);
    quillEditor.setSelection(cursorPosition + user?.username?.length + 1);

    setMentionListVisible(false);
  }, [cursorPosition]);

  const handleComment = useCallback((e) => {
    const commentData = removeHtmlTagsAndEntities(editorRef?.current?.value);
    if (commentData) {
      setNewComment(e);
      setIsDisable(false);
    } else {
      setIsDisable(true);
      setNewComment(messg);
    }
  }, [messg]);

  const handleChange = useCallback((e) => {
    const searchedText = e?.target?.value?.trim();
    setSortUser(searchedText);
    const filteredUsers = data?.data?.data?.responseData?.filter(user =>
      user?.username?.toLowerCase()?.includes(searchedText?.toLowerCase())
    );
    setSelectedUserForTag(filteredUsers);
  }, [data]);

  const handleMentionClose = useCallback(() => {
    setSortUser("");
    setMentionListVisible(false);
  }, []);

  useEffect(() => {
    if (data) setSelectedUserForTag(data?.data?.data?.responseData);
  }, [data]);

  return (
    <div className="d-flex align-items-center">
      <CommentEditor
        isMobile={isMobile}
        comment={newComment}
        handleComment={handleComment}
        handleMention={handleMention}
        setElementPos={setElementPos}
        editorRef={editorRef}
        mentionListVisible={mentionListVisible}
        anchorEl={anchorEl}
        elementPos={elementPos}
        handleMentionClose={handleMentionClose}
        sortUser={sortUser}
        handleChange={handleChange}
        selectedUserForTag={selectedUserForTag}
        handleUserSelect={handleUserSelect}
        handleSave={handleSave}
        handleCancel={() => setShowEdit(false)}
        isDisable={isDisable}
        containerId="forAnchor1"
      />
    </div>
  );
})
